import { useState, useEffect, useCallback } from 'react';
import { DailyLogForm } from './DailyLogForm';
import { DailyLogCard } from './DailyLogCard';
import type { DailyLog } from './types';
import apiClient from '@/lib/api/client';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { ToastContainer } from '@/components/common/Toast';
import { useToast } from '@/lib/hooks/useToast';

interface DailyLogsTabProps {
  jobId: string;
}

export const DailyLogsTab = ({ jobId }: DailyLogsTabProps) => {
  const [logs, setLogs] = useState<DailyLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingLog, setEditingLog] = useState<DailyLog | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const { toasts, removeToast, toast } = useToast();

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError('');
      const { data } = await apiClient.get(`/jobs/${jobId}/daily-logs`);
      setLogs(data.data || []);
    } catch {
      setLoadError('Failed to load daily logs.');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleSave = async () => {
    await loadLogs();
    setShowForm(false);
    setEditingLog(null);
  };

  const handleEdit = (log: DailyLog) => {
    setEditingLog(log);
    setShowForm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDeleteId) return;
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    try {
      await apiClient.delete(`/jobs/${jobId}/daily-logs/${id}`);
      toast.success('Daily log deleted.');
      await loadLogs();
    } catch {
      toast.error('Failed to delete daily log.');
    }
  };

  const handleToggleShare = async (log: DailyLog) => {
    const next = log.visible_to_client ? 0 : 1;
    try {
      await apiClient.patch(`/jobs/${jobId}/daily-logs/${log.id}/share`, { visible_to_client: next });
      setLogs(prev => prev.map(l => l.id === log.id ? { ...l, visible_to_client: next } : l));
      toast.success(next ? 'Log shared — the client can now see it in their portal.' : 'Log hidden from the client portal.');
    } catch {
      toast.error('Failed to update sharing.');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingLog(null);
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-40 bg-gray-100 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <span className="material-symbols-outlined text-5xl text-red-300">error</span>
        <p className="text-gray-500 font-medium">{loadError}</p>
        <button
          onClick={loadLogs}
          className="text-sm font-semibold text-primary border border-primary/30 rounded-lg px-4 py-2 hover:bg-primary/5"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-gray-900 text-[22px] font-bold leading-tight tracking-[-0.015em]">
            Daily Logs
          </h2>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors"
            >
              <span className="material-symbols-outlined text-sm">add</span>
              Add Daily Log
            </button>
          )}
        </div>

        {showForm && (
          <div className="mb-6">
            <DailyLogForm
              jobId={jobId}
              log={editingLog}
              onSave={handleSave}
              onCancel={handleCancel}
              onError={(msg) => toast.error(msg)}
            />
          </div>
        )}

        {logs.length === 0 && !showForm ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
            <span className="material-symbols-outlined text-6xl text-gray-300 mb-4 block">
              description
            </span>
            <h3 className="text-base font-semibold text-gray-900 mb-2">
              No daily logs yet
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Start tracking daily progress, hours, and site conditions
            </p>
            <button
              onClick={() => setShowForm(true)}
              className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors text-sm font-semibold"
            >
              Create First Log
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {logs.map((log) => (
              <DailyLogCard
                key={log.id}
                log={log}
                onEdit={handleEdit}
                onDelete={(id) => setConfirmDeleteId(id)}
                onToggleShare={handleToggleShare}
              />
            ))}
          </div>
        )}
      </div>

      {confirmDeleteId !== null && (
        <ConfirmDialog
          title="Delete Daily Log"
          message="Are you sure you want to delete this daily log? This cannot be undone."
          confirmLabel="Delete"
          variant="danger"
          onConfirm={handleDeleteConfirm}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};


